import { type FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';

import { type JwtPayload } from 'jwt-decode';

import { useAppDispatch } from '@editor2/store';
import { type RootState } from '@editor2/reducers/root';
import { fetchAccessToken } from '@editor2/reducers/jwtSlice';
import { useJwtTokenPayload } from '@editor2/hooks/useJwtTokenPayload';
import { useQuery } from '@editor2/hooks/useQuery';
import { debugLog } from '@editor2/services/debugService';

interface OneTimeTokenPayload extends JwtPayload {
	extra: {
		initial_view: { [key: string]: null | string | number | boolean };
	};
}

const JwtAuthGateway: FC = () => {
	const dispatch = useAppDispatch();

	const token = useSelector((state: RootState) => state.jwt.token);

	const pathParams = useParams<{ courseId?: string }>();
	const { courseId } = pathParams;

	const queryParams = useQuery();
	const url = queryParams.get('url') || '';

	const oneTimeToken = queryParams.get('jwt');
	const oneTimeTokenPayload = useJwtTokenPayload<OneTimeTokenPayload>(oneTimeToken);

	const [isConfigApplied, setConfigApplied] = useState(false);
	const [error, setError] = useState<boolean | Error>(!oneTimeTokenPayload);

	useEffect(() => {
		if (!courseId || !oneTimeTokenPayload) {
			debugLog('auth', 'JwtAuthGateway: missing courseId or invalid one-time token', {
				courseId,
				hasOneTimeToken: !!oneTimeToken,
				hasPayload: !!oneTimeTokenPayload
			});
			return;
		}

		// Clear any previously stored token to avoid stale auth state
		const previousToken = localStorage.getItem('accessToken');
		if (previousToken) {
			localStorage.removeItem('accessToken');
		}

		dispatch(fetchAccessToken(oneTimeToken))
			.unwrap()
			.then((accessToken) => {
				localStorage.setItem('accessToken', accessToken);
				setConfigApplied(true);
			})
			.catch((error) => {
				debugLog('auth', `Failed to fetch access token for course ${courseId}`, error);
				setError(error instanceof Error ? error : new Error(String(error)));
				console.error(`Cannot fetch access token for the course ${courseId}`, error);
			});
	}, [courseId, dispatch, oneTimeToken, oneTimeTokenPayload]);

	return (
		<>
			<div>
				{error && (
						<div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif' }}>
							<p style={{ color: '#d32f2f', fontWeight: 'bold' }}>
								Your redirection link was broken. Please go back to your webtext and re-click the Editor link.
							</p>
							<details style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>
								<summary style={{ cursor: 'pointer' }}>Debug info (for support)</summary>
								<pre style={{ marginTop: '8px', whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
	{JSON.stringify({
		courseId,
		error: error instanceof Error ? error.message : String(error),
		timestamp: new Date().toISOString()
	}, null, 2)}
								</pre>
							</details>
						</div>
					)}

				{!error && isConfigApplied && token && <Redirect to={`/courses/${courseId}${url}`} />}
			</div>
		</>
	);
};

export default JwtAuthGateway;
